import React, {
    createContext,
    MouseEvent,
    MutableRefObject,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { v4 as uuid } from 'uuid';
import { useDragEvents } from './useDragEvents';
import { isCtrlKey } from 'packages/helpers/hotKeys';

const padding = 4;
const height = 44;

const checkIfNewTrack = (clientY, mouseTop, containerBoundingBoxRef) => {
    const threshold = padding / height;
    const maxMouseTop = containerBoundingBoxRef.current.bottom;

    let x = (mouseTop.current / height) % 1;
    x = x < 0.5 ? x : 1 - x;

    return x < threshold && clientY < maxMouseTop - 10;
};

type ElementPosition = {
    top: number;
    left: number;
};

// TODO: Refactor both dragging and resizing hooks, a lot of spaghetti code
// +1
export const useDragging = ({
    elementRef,
    containerRef,
    scrollContainerRef,
    tracksRef,
    onDragCb,
    onDrop,
    onCancel = () => {},
    handleMouseUp,
    reactToPeers = false,
}): [MutableRefObject<boolean>, (evt: MouseEvent, ...args: unknown[]) => void] => {
    const {
        dragPeers,
        dragStartPeers,
        dragEndPeers,
        subscribeToPeerDrag,
        subscribeToPeerDragStart,
        subscribeToPeerDragEnd,
    } = useContext(DraggingContext);
    const boundingBoxRef = useRef(null);
    const containerBoundingBoxRef = useRef(null);
    const isDragging = useRef(false);
    const mouseTop = useRef<number | null>(null);
    const clientYRef = useRef(0);
    const timeoutRef = useRef<NodeJS.Timeout | number>(0);
    const isBetweenTracks = useRef(false);
    const initialPositionOfElement = useRef<ElementPosition | null>(null);
    const elementCloneRef = useRef<HTMLElement | null>(null);
    const rafId = useRef<number | null>(null);
    const lastMove = useRef<[number, number]>([0, 0]);
    const eventCanceled = useRef(false);
    const peerIdRef = useRef<string>(uuid());

    const getScroll = useCallback(
        () => [scrollContainerRef?.current?.scrollLeft ?? 0, scrollContainerRef?.current?.scrollTop ?? 0],
        [scrollContainerRef],
    );
    const initialScroll = useRef(getScroll());

    const handleDragStart = useCallback(() => {
        boundingBoxRef.current = elementRef.current.getBoundingClientRect();
        containerBoundingBoxRef.current = containerRef.current.getBoundingClientRect();
        elementCloneRef.current = elementRef.current.cloneNode(true);
        initialScroll.current = getScroll();

        const initialLeftPercentage =
            ((boundingBoxRef.current.left - containerBoundingBoxRef.current.left) /
                containerBoundingBoxRef.current.width) *
            100;
        initialPositionOfElement.current = {
            top: boundingBoxRef.current.top - containerBoundingBoxRef.current.top,
            left: initialLeftPercentage,
        };

        timeoutRef.current = setTimeout(() => {
            isDragging.current = true;
            elementRef.current.classList.add('dragging');
            elementCloneRef.current?.classList?.add('ghost-element');

            containerRef.current.insertBefore(elementCloneRef.current, elementRef.current);
            containerRef.current.parentNode.classList.add('dragging');
            tracksRef.current.classList.add('dragging');
        }, 170);
    }, []);

    const onDragStart = useCallback(() => {
        dragStartPeers(peerIdRef.current);
        handleDragStart();
    }, [handleDragStart]);

    const handleDrag = useCallback(
        (deltaX, deltaY, evt = null) => {
            lastMove.current = [deltaX, deltaY];

            const elementTop = boundingBoxRef.current.top - containerBoundingBoxRef.current.top;
            const elementLeft = boundingBoxRef.current.left - containerBoundingBoxRef.current.left;
            const newScroll = getScroll();
            const scrollDiff = [newScroll[0] - initialScroll.current[0], newScroll[1] - initialScroll.current[1]];

            const maxTop = containerBoundingBoxRef.current.height - boundingBoxRef.current.height;
            const maxLeft = Math.max(containerBoundingBoxRef.current.width - boundingBoxRef.current.width, 0);
            const top = Math.min(Math.max(elementTop + deltaY + scrollDiff[1], 0), maxTop);
            const left = Math.min(Math.max(elementLeft + deltaX + scrollDiff[0], 0), maxLeft);

            if (evt) {
                clientYRef.current = evt.clientY;
            }

            mouseTop.current = clientYRef.current - containerBoundingBoxRef.current.top + scrollDiff[1];
            isBetweenTracks.current = checkIfNewTrack(clientYRef.current, mouseTop, containerBoundingBoxRef);

            rafId.current = requestAnimationFrame(() => {
                if (isDragging.current && elementRef.current) {
                    elementRef.current.style.top = `${top}px`;
                    elementRef.current.style.left = `${left}px`;
                }
            });

            return left;
        },
        [elementRef, getScroll],
    );

    const onDrag = useCallback(
        (deltaX, deltaY, evt = null) => {
            dragPeers(peerIdRef.current, deltaX, deltaY);
            const left = handleDrag(deltaX, deltaY, evt);

            if (isDragging.current) {
                onDragCb(left, boundingBoxRef.current);
            }
        },
        [handleDrag, dragPeers, onDragCb],
    );

    useEffect(() => {
        if (!reactToPeers) {
            return;
        }

        const unsubscribe = subscribeToPeerDragStart((peerId) => {
            if (peerIdRef.current !== peerId) {
                handleDragStart();
            }
        });

        return () => {
            unsubscribe();
        };
    }, [handleDragStart, reactToPeers]);

    useEffect(() => {
        if (!reactToPeers) {
            return;
        }

        const unsubscribe = subscribeToPeerDrag((peerId, deltaX, deltaY) => {
            if (peerId !== peerIdRef.current) {
                handleDrag(deltaX, deltaY);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [handleDrag, subscribeToPeerDrag, reactToPeers]);

    // a copy of onDragEnd, but without using onDrop callback and mouseEvent logic
    const handlePeerDragEnd = useCallback(() => {
        if (eventCanceled.current) {
            eventCanceled.current = false;

            return;
        }

        if (!isDragging.current) {
            clearTimeout(timeoutRef.current);

            if (rafId.current) {
                cancelAnimationFrame(rafId.current as number);
            }

            return;
        }

        elementCloneRef.current?.remove();
        containerRef.current.parentNode.classList.remove('dragging');
        tracksRef.current.classList.remove('dragging');

        setTimeout(() => {
            elementRef.current.style.top = `${initialPositionOfElement.current.top}px`;
            elementRef.current.classList.remove('dragging');
            isDragging.current = false;
            initialPositionOfElement.current = null;
        });
    }, []);

    useEffect(() => {
        if (!reactToPeers) {
            return;
        }

        const unsubscribe = subscribeToPeerDragEnd((peerId) => {
            if (peerIdRef.current !== peerId) {
                handlePeerDragEnd();
            }
        });

        return () => {
            unsubscribe();
        };
    }, [handlePeerDragEnd, reactToPeers]);

    const onDragEnd = (evt: MouseEvent) => {
        if (eventCanceled.current) {
            eventCanceled.current = false;

            return;
        }

        dragEndPeers(peerIdRef.current);

        if (!isDragging.current) {
            clearTimeout(timeoutRef.current);

            if (rafId.current) {
                cancelAnimationFrame(rafId.current as number);
            }

            handleMouseUp(evt);

            return;
        }

        const boundingBox = elementRef.current.getBoundingClientRect();
        const { clientX, clientY } = evt;

        elementCloneRef.current?.remove();
        containerRef.current.parentNode.classList.remove('dragging');
        tracksRef.current.classList.remove('dragging');

        setTimeout(() => {
            const elementUnderCursor = document.elementFromPoint(clientX, clientY) as HTMLElement;
            const closestElement = elementUnderCursor?.closest('.timeline__element') as HTMLElement;

            if (!closestElement && !elementUnderCursor) {
                elementRef.current.style.top = `${boundingBoxRef.current.top - containerBoundingBoxRef.current.top}px`;
                elementRef.current.style.left = `${
                    boundingBoxRef.current.left - containerBoundingBoxRef.current.left
                }px`;
                elementRef.current.classList.remove('dragging');
                isDragging.current = false;

                return;
            }

            elementRef.current.style.top = `${initialPositionOfElement.current.top}px`;

            const groupId = elementUnderCursor.dataset.groupId || (closestElement && closestElement.dataset.groupId);

            const isCtrlHeld = isCtrlKey(evt);
            const onGroup = closestElement && closestElement.dataset.isGroup === 'true';
            const inGroup =
                elementUnderCursor.dataset.groupId || (closestElement && !onGroup && !!closestElement.dataset.groupId);
            const isExpanded = closestElement && closestElement.dataset.isExpanded === 'true';
            const onElement = !onGroup && closestElement && closestElement.dataset.id;

            const left = boundingBox.left - containerBoundingBoxRef.current.left;
            const leftPercentage = 100 / (containerBoundingBoxRef.current.width / left);

            elementRef.current.classList.remove('dragging');
            isDragging.current = false;
            initialPositionOfElement.current = null;

            if (!mouseTop.current) {
                handleMouseUp(evt);

                return;
            }

            const dropParams = {
                groupId,
                isCtrlHeld,
                onGroup,
                inGroup,
                isExpanded,
                onElement,
            };

            onDrop(mouseTop.current, leftPercentage, dropParams, isBetweenTracks.current);
        });
    };

    const cleanUp = useCallback(() => {
        clearTimeout(timeoutRef.current);

        if (rafId.current) {
            cancelAnimationFrame(rafId.current as number);
        }

        elementRef.current.classList.remove('dragging');
        elementRef.current.style.top = `${initialPositionOfElement.current?.top}px`;
        initialPositionOfElement.current = null;

        elementCloneRef.current?.remove();
        containerRef.current.parentNode.classList.remove('dragging');
        tracksRef.current.classList.remove('dragging');
    }, []);

    const onDragCancel = useCallback(() => {
        isDragging.current = false;
        eventCanceled.current = true;

        onCancel();
        cleanUp();
    }, [onCancel, cleanUp]);

    useEffect(() => {
        const onScroll = () => {
            if (isDragging.current) {
                onDrag(...lastMove.current);
            }
        };

        scrollContainerRef.current?.addEventListener('scroll', onScroll);
        const ref = scrollContainerRef.current;

        return () => {
            ref?.removeEventListener('scroll', onScroll);
        };
    }, [onDrag, scrollContainerRef, lastMove]);

    const [onMouseDown] = useDragEvents({ onDragStart, onDrag, onDragEnd, cancelCb: onDragCancel });

    return [isDragging, onMouseDown];
};

const DraggingContext = createContext({
    dragPeers: (_peerId: string, _deltaX: number, _deltaY: number) => {},
    dragStartPeers: (_peerId: string) => {},
    dragEndPeers: (_peerId: string) => {},
    subscribeToPeerDrag: (_cb: (peerId: string, deltaX: number, deltaY: number) => void) => () => {},
    subscribeToPeerDragStart: (_cb: (peerId: string) => void) => () => {},
    subscribeToPeerDragEnd: (_cb: (peerId: string) => void) => () => {},
});

export const DraggingProvider = ({ children, reactToPeers }: { children: ReactNode; reactToPeers?: boolean }) => {
    const dragSubscriptionsRef = useRef(new Set<(peerId: string, deltaX: number, deltaY: number) => void>());
    const dragStartSubscriptionsRef = useRef(new Set<(peerId: string) => void>());
    const dragEndSubscriptionsRef = useRef(new Set<(peerId: string) => void>());

    const dragPeers = useCallback((peerId: string, deltaX: number, deltaY: number) => {
        dragSubscriptionsRef.current.forEach((cb) => cb(peerId, deltaX, deltaY));
    }, []);

    const dragStartPeers = useCallback((peerId: string) => {
        dragStartSubscriptionsRef.current.forEach((cb) => cb(peerId));
    }, []);

    const dragEndPeers = useCallback((peerId: string) => {
        dragEndSubscriptionsRef.current.forEach((cb) => cb(peerId));
    }, []);

    const subscribeToPeerDrag = useCallback(
        (cb: (peerId: string, deltaX: number, deltaY: number) => void) => {
            if (reactToPeers) {
                dragSubscriptionsRef.current.add(cb);
            }

            return () => {
                dragSubscriptionsRef.current.delete(cb);
            };
        },
        [reactToPeers],
    );

    const subscribeToPeerDragStart = useCallback(
        (cb: (peerId: string) => void) => {
            if (reactToPeers) {
                dragStartSubscriptionsRef.current.add(cb);
            }

            return () => {
                dragStartSubscriptionsRef.current.delete(cb);
            };
        },
        [reactToPeers],
    );

    const subscribeToPeerDragEnd = useCallback(
        (cb: (peerId: string) => void) => {
            if (reactToPeers) {
                dragEndSubscriptionsRef.current.add(cb);
            }

            return () => {
                dragEndSubscriptionsRef.current.delete(cb);
            };
        },
        [reactToPeers],
    );

    const value = useRef({
        dragPeers,
        dragStartPeers,
        dragEndPeers,
        subscribeToPeerDrag,
        subscribeToPeerDragStart,
        subscribeToPeerDragEnd,
    });

    useEffect(() => {
        return () => {
            dragSubscriptionsRef.current.clear();
            dragStartSubscriptionsRef.current.clear();
        };
    }, []);

    return <DraggingContext.Provider value={value.current}>{children}</DraggingContext.Provider>;
};
